import React from 'react';
import { FileText, ListChecks, BadgeCheck, Lightbulb, PackageCheck } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO } from '@/config/links';

const PrintShippingLabel = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Print Shipping Labels"
        description="Generate and print compliant shipping labels from Boxd Logistics in seconds, with multi-courier support and bulk workflows."
        keywords="print shipping label, shipping label generator, ecommerce shipping label, awb label"
        canonical="https://boxdlogistics.in/print-label"
      />
      <Navbar />

      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        ></div>
        <div className="absolute -top-12 right-0 w-72 h-72 bg-gradient-to-br from-blue-300/20 to-blue-700/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-900 px-4 py-2 text-sm font-semibold mb-6">
            <FileText className="w-4 h-4" />
            Print Shipping Labels
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Print Shipping Labels</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl">
            Boxd Logistics makes the shipping process easy and efficient, including the ability to print shipping labels conveniently from your home or office.
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <ListChecks className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">How to Print Shipping Labels with Boxd Logistics</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">
              Printing shipping labels with Boxd Logistics is a straightforward process that ensures your shipments are properly labeled and ready for delivery.
            </p>
            <ol className="list-decimal pl-6 space-y-3 text-gray-700">
              <li>Log into your Boxd Logistics account. If you do not have one, you can create one quickly and easily.</li>
              <li>Create a shipment by entering sender and recipient addresses, package dimensions, and weight.</li>
              <li>Click on ship now and select the shipping service that suits your needs .</li>
              <li>Go to not picked section bulk select orders then click on &quot;Download Label&quot; button.</li>
              <li>Print the label in printable format.</li>
              <li>Attach the label securely to your package so it is visible and legible.</li>
            </ol>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <BadgeCheck className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Benefits of Printing Labels with Boxd</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li><strong>Convenience:</strong> Print labels from your home or office without visiting a shipping center.</li>
              <li><strong>Accuracy:</strong> Essential shipping information is included on the label, reducing errors.</li>
              <li><strong>Efficiency:</strong> Generate and print labels in minutes.</li>
              <li><strong>Tracking:</strong> Each label includes a unique tracking number for shipment visibility.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <Lightbulb className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Label Printing Tips</h2>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li>Use a high-quality printer (laser or thermal) for clear and durable labels.</li>
              <li>Use adhesive label paper designed for shipping labels.</li>
              <li>Check print settings for label size and orientation before printing.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <PackageCheck className="w-5 h-5 text-blue-900 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Ready to Ship?</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Printing shipping labels with Boxd Logistics is one of many convenient features designed to simplify your shipping workflow. If you need help with labels or any shipping task, contact our support team at{' '}
              <a href={SUPPORT_EMAIL_MAILTO} className="text-blue-900 hover:text-blue-950 font-semibold">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PrintShippingLabel;